import { useParams } from 'react-router-dom';

import type { EnrollmentStatusResponse } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Badge, Card, CardBody, Spinner } from '@fluxora/ui';

import { isNotFound } from '../api/bff-client';
import { useEnrollmentStatusQuery } from '../api/status.queries';
import { NewOccupantForm } from '../components/NewOccupantForm';
import { nextStepsKeyFor } from '../services/new-occupant.service';

/**
 * Anonymous enrollment status page (E-SF07).
 *
 * Reached at `/status/:token` — the link in every pre-activation email. NO login,
 * NO session: the single-purpose, expiring, unguessable token IS the capability
 * (AC2). Renders the lifecycle (pending → scheduled → active) and, for a
 * rejection, the next steps — including the switch-hold self-serve resolution
 * form (AC1).
 *
 * All five async states are handled: no-token guard, loading (Spinner), error
 * (Alert — an invalid/expired 404 reads as an honest "this link isn't valid"),
 * empty is not applicable (a token resolves to exactly one lifecycle), and the
 * loaded lifecycle.
 */
export function StatusPage(): JSX.Element {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();

  const status = useEnrollmentStatusQuery(token);

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-5 px-4 py-10">
      <h1 className="text-xl font-bold text-primary">{t('shop.status.heading')}</h1>

      {token === undefined ? (
        <Alert variant="warning" title={t('shop.status.invalid.title')} role="status">
          {t('shop.status.invalid.body')}
        </Alert>
      ) : null}

      {status.isPending && token !== undefined ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" label={t('shop.status.loading')} />
        </div>
      ) : null}

      {status.isError ? (
        <Alert
          variant={isNotFound(status.error) ? 'warning' : 'danger'}
          title={isNotFound(status.error) ? t('shop.status.invalid.title') : t('shop.status.error.title')}
          role={isNotFound(status.error) ? 'status' : 'alert'}
        >
          {isNotFound(status.error) ? t('shop.status.invalid.body') : t('shop.status.error.body')}
        </Alert>
      ) : null}

      {status.isSuccess && token !== undefined ? (
        <StatusBody status={status.data} token={token} />
      ) : null}
    </main>
  );
}

const STATE_VARIANT: Record<
  EnrollmentStatusResponse['state'],
  'primary' | 'success' | 'danger'
> = {
  PENDING: 'primary',
  SCHEDULED: 'primary',
  ACTIVE: 'success',
  REJECTED: 'danger',
};

/** The loaded lifecycle view. Presentational — all copy is `t()`, keyed by state. */
function StatusBody({
  status,
  token,
}: {
  readonly status: EnrollmentStatusResponse;
  readonly token: string;
}): JSX.Element {
  const { t } = useTranslation();
  const isSwitch = status.enrollmentType === 'SWITCH';

  return (
    <div className="flex flex-col gap-5">
      <Card variant="glass" padding="md">
        <CardBody className="flex flex-col gap-3 p-0">
          <div className="flex items-center gap-3">
            <Badge variant={STATE_VARIANT[status.state]}>{t(`shop.status.state.${status.state}.badge`)}</Badge>
            <span className="text-sm font-medium text-foreground">
              {t(`shop.status.state.${status.state}.title`)}
            </span>
          </div>

          {status.state === 'SCHEDULED' && status.serviceStartDate !== null ? (
            <p className="text-sm text-foreground/80">
              {t(isSwitch ? 'shop.status.state.SCHEDULED.switchBody' : 'shop.status.state.SCHEDULED.moveInBody', {
                date: status.serviceStartDate,
              })}
            </p>
          ) : null}

          {status.state === 'ACTIVE' && status.serviceStartDate !== null ? (
            <p className="text-sm text-foreground/80">
              {t('shop.status.state.ACTIVE.body', { date: status.serviceStartDate })}
            </p>
          ) : null}

          {status.state === 'PENDING' ? (
            <p className="text-sm text-foreground/80">{t('shop.status.state.PENDING.body')}</p>
          ) : null}

          {status.state === 'SCHEDULED' && isSwitch && status.rescissionDeadline !== null ? (
            <p className="text-xs text-foreground/60">
              {t('shop.status.rescission', { date: status.rescissionDeadline })}
            </p>
          ) : null}
        </CardBody>
      </Card>

      {status.state === 'REJECTED' && status.rejection !== null ? (
        <div className="flex flex-col gap-4">
          <Alert
            variant="danger"
            title={t(`shop.status.rejected.${status.rejection.reason}.title`)}
            role="alert"
          >
            {t(nextStepsKeyFor(status.rejection.reason))}
          </Alert>
          {status.rejection.resolvable ? <NewOccupantForm token={token} /> : null}
        </div>
      ) : null}
    </div>
  );
}
